import { Injectable } from '@angular/core';
import { Store, StoreConfig } from '@datorama/akita';

import { RxIntuitiveTeethDiagramState, UnitTypesInBridge } from '../models';
import { IntuitiveBridge } from '../models/intuitive-bridge';
import { getNewTooth, Tooth } from '@modules/teeth-diagram/models/tooth';

const createInitialState = (): RxIntuitiveTeethDiagramState => ({
	selectedToothIds: [],
	toothLastTouchedAt: {},
	selectedBridge: undefined,
	dismissedGuidanceBanner: false,
	isSelectedBridgeSubmitted: false
});

@Injectable()
@StoreConfig({ name: 'rx-intuitive-teeth-diagram', resettable: true })
export class RxIntuitiveTeethDiagramStore extends Store<RxIntuitiveTeethDiagramState> {
	constructor() {
		super(createInitialState());
	}

	selectBridge(bridge: IntuitiveBridge, focusOnTooth?: number): void {
		const selectedToothByDefault = focusOnTooth ?? bridge.outerLeftToothId;
		const newSelection = selectedToothByDefault ? [selectedToothByDefault] : [];

		this.update({
			selectedBridge: bridge,
			selectedToothIds: newSelection,
			dismissedGuidanceBanner: false,
			isSelectedBridgeSubmitted: false
		});
	}

	deselectBridge(): void {
		this.update({ selectedBridge: undefined, selectedToothIds: [], dismissedGuidanceBanner: false, isSelectedBridgeSubmitted: false });
	}

	dismissGuidanceBanner(): void {
		this.update({ dismissedGuidanceBanner: true });
	}

	assignBridgeIndication(toothIds: number[], unitTypeInBridge: UnitTypesInBridge): void {
		this.update(state => {
			if (!state.selectedBridge) {
				return state;
			}

			const updatedTeeth = state.selectedBridge.teeth.map(t => {
				if (!t.ToothID || !toothIds.includes(t.ToothID)) {
					return t;
				}

				if (t.ToothInBridgeTypeID === unitTypeInBridge) {
					return t;
				}

				return getNewTooth({
					ToothID: t.ToothID,
					UnitTypeID: t.UnitTypeID,
					BridgeIndex: t.BridgeIndex,
					ToothInBridgeTypeID: unitTypeInBridge
				});
			});

			return { selectedBridge: { ...state.selectedBridge, teeth: updatedTeeth } };
		});
	}

	updateBridgeTeethByIds(toothIds: number[], partialToothUpdate: Partial<Tooth>): void {
		this.update(state => {
			if (!state.selectedBridge) {
				return state;
			}

			const updatedTeeth = state.selectedBridge.teeth.map(t =>
				t.ToothID != null && toothIds.includes(t.ToothID) ? { ...t, ...partialToothUpdate } : t
			);

			return { selectedBridge: { ...state.selectedBridge, teeth: updatedTeeth } };
		});
	}

	markRecentlyEditedTeeth(toothIds: number[]): void {
		if (toothIds.length === 0) {
			return;
		}

		const timestamp = Date.now();

		this.update(state => {
			const toothLastTouchedAt = { ...state.toothLastTouchedAt };

			for (const id of toothIds) {
				toothLastTouchedAt[id] = timestamp;
			}

			return { ...state, toothLastTouchedAt };
		});
	}

	clearRecentlyEditedTeeth(toothIds: number[]): void {
		if (toothIds.length === 0) {
			return;
		}

		this.update(state => {
			const toothLastTouchedAt = { ...state.toothLastTouchedAt };

			for (const id of toothIds) {
				delete toothLastTouchedAt[id];
			}

			return { ...state, toothLastTouchedAt };
		});
	}

	setBridgeSubmitted(): void {
		this.update({ isSelectedBridgeSubmitted: true });
	}

	reset(): void {
		this.deselectBridge();
		this.update({toothLastTouchedAt: {}});
	}
}
